import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box as MuiBox, useTheme } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ClearIcon from '@mui/icons-material/Clear';
import PropTypes from 'prop-types';
import useFormDraft from '../../hooks/useFormDraft';
import LoadDraftButton from '../../components/LoadDraftButton';
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  IconButton,
  InputLabel,
  FormControl,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Card,
  Typography,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDDatePicker from 'components/MDDatePicker';
import RichTextEditor from 'components/RichTextEditor';
import { formatDateForDisplay } from '../../utils/dateUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DataTable from 'examples/Tables/DataTable';
import EditIcon from '@mui/icons-material/Edit';
import swal from 'sweetalert2';

const SURGICAL_OPTIONS = [
  { value: 'surgery1', label: 'Surgery 1' },
  { value: 'surgery2', label: 'Surgery 2' },
  { value: 'surgery3', label: 'Surgery 3' },
];

const selectBoxHeight = 40; // Adjust as needed

// Function to format date to DD/MM/YYYY format for display
const formatDate = (dateString) => {
  return formatDateForDisplay(dateString) || '-';
};

function SurgicalHistoryForm({
  patientId,
  spouseId,
  loading,
  onSubmit,
  handleClear,
  // previousPatientData = [
  //   { id: 1, surgery: 'Surgery 1', notes: 'Appendectomy in 2015' },
  //   { id: 2, surgery: 'Surgery 2', notes: 'Gallbladder removal in 2018' },
  // ],
  previousPatientData = [],
  // previousSpouseData = [
  //   { id: 1, surgery: 'Surgery 3', notes: 'Knee replacement in 2020' },
  //   { id: 2, surgery: 'Surgery 1', notes: 'C-section in 2017' },
  // ],
  previousSpouseData = [],
  onDeletePatient,
  onDeleteSpouse,
  onEditPatient,
  onEditSpouse,
  opdParams = {},
}) {

  // console.log('SurgicalHistoryForm rendered with opdParams:', opdParams);
  // State management
  const [genericTab, setGenericTab] = useState(0);
  const [editingRecord, setEditingRecord] = useState(null);
  const theme = useTheme();
  
  // Draft system for patient and spouse surgical history
  const patientDraft = useFormDraft(`surgical_history_patient_${patientId}`, 1000);
  const spouseDraft = useFormDraft(`surgical_history_spouse_${spouseId}`, 1000);
  
  const [rowsPatient, setRowsPatient] = useState([{
    'surgical-history': '',
    'history_date': '',
    'surgical-notes': '',
    'param_group_member_id': '',
    'param_group_member_option_id': '',
    'param_group_id': ''
  }]);
  const [rowsSpouse, setRowsSpouse] = useState([{
    'surgical-history': '',
    'history_date': '',
    'surgical-notes': '',
    'param_group_member_id': '',
    'param_group_member_option_id': '',
    'param_group_id': ''
  }]);

  // Handler to load patient draft on button click
  const handleLoadPatientDraft = async () => {
    const draft = await patientDraft.loadDraftData();
    if (draft) {
      setRowsPatient(draft);
    }
  };

  // Handler to load spouse draft on button click
  const handleLoadSpouseDraft = async () => {
    const draft = await spouseDraft.loadDraftData();
    if (draft) {
      setRowsSpouse(draft);
    }
  };

  // Auto-save patient surgical history
  useEffect(() => {
    if (!patientId) return;
    const hasData = rowsPatient.some(row => 
      row['surgical-history'] || row['history_date'] || row['surgical-notes']
    );
    if (hasData) {
      patientDraft.autoSave(rowsPatient);
    }
  }, [rowsPatient, patientId]);

  // Auto-save spouse surgical history
  useEffect(() => {
    if (!spouseId) return;
    const hasData = rowsSpouse.some(row => 
      row['surgical-history'] || row['history_date'] || row['surgical-notes']
    );
    if (hasData) {
      spouseDraft.autoSave(rowsSpouse);
    }
  }, [rowsSpouse, spouseId]);

  // Handlers for each tab
  const handleChange = async (tabIdx, idx, field, value) => {
    if (tabIdx === 0) {
      setRowsPatient((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
    } else {
      setRowsSpouse((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
    }

    if (field === 'history_date' && value) {
      const currentRows = tabIdx === 0 ? rowsPatient : rowsSpouse;
      const blanks = currentRows.filter((row, i) => i !== idx && !row['history_date']);
      if (blanks.length > 0) {
        const result = await swal.fire({
          title: 'Apply Date to All?',
          text: `Do you want to apply this date to all ${blanks.length} empty date field(s)?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#00798E',
          cancelButtonColor: '#6c757d',
          confirmButtonText: 'Yes, apply to all',
          cancelButtonText: 'No, keep empty',
        });
        if (result.isConfirmed) {
          if (tabIdx === 0) {
            setRowsPatient((prev) => prev.map((row, i) => (i !== idx && !row['history_date'] ? { ...row, history_date: value } : row)));
          } else {
            setRowsSpouse((prev) => prev.map((row, i) => (i !== idx && !row['history_date'] ? { ...row, history_date: value } : row)));
          }
          await swal.fire({ title: 'Date Applied', text: 'The date has been applied to all empty fields', icon: 'success', timer: 2000, showConfirmButton: false });
        }
      }
    }
  };
  const handleAddRow = (tabIdx) => {
    if (tabIdx === 0) {
      setRowsPatient((prev) => [...prev, {
        'surgical-history': '',
        'surgery-date': '',
        'surgical-notes': '',
        'param_group_member_id': '',
        'param_group_member_option_id': '',
        'param_group_id': ''
      }]);
    } else {
      setRowsSpouse((prev) => [...prev, {
        'surgical-history': '',
        'surgery-date': '',
        'surgical-notes': '',
        'param_group_member_id': '',
        'param_group_member_option_id': '',
        'param_group_id': ''
      }]);
    }
  };
  const handleRemoveRow = (tabIdx, idx) => {
    if (tabIdx === 0) {
      setRowsPatient((prev) => prev.filter((_, i) => i !== idx));
    } else {
      setRowsSpouse((prev) => prev.filter((_, i) => i !== idx));
    }
  };
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (genericTab === 0) {
      const result = await patientDraft.handleSubmitWithDraft(
        async (data) => {
          await onSubmit(data, 'patient');
          return { success: true };
        },
        rowsPatient
      );
    } else {
      const result = await spouseDraft.handleSubmitWithDraft(
        async (data) => {
          await onSubmit(data, 'spouse');
          return { success: true };
        },
        rowsSpouse
      );
    }
  };
  const handleClearAll = () => {
    if (genericTab === 0) {
      setRowsPatient([{
        'surgical-history': '',
        'history_date': '',
        'surgical-notes': '',
        'param_group_member_id': '',
        'param_group_member_option_id': '',
        'param_group_id': ''
      }]);
    } else {
      setRowsSpouse([{
        'surgical-history': '',
        'history_date': '',
        'surgical-notes': '',
        'param_group_member_id': '',
        'param_group_member_option_id': '',
        'param_group_id': ''
      }]);
    }
  };

  // Log the received data
  React.useEffect(() => {
    console.log('Previous Patient Data:', previousPatientData);
    console.log('Previous Spouse Data:', previousSpouseData);
  }, [previousPatientData, previousSpouseData]);

  // Render previous data table for each tab
  const renderPreviousData = () => {
    if (genericTab === 0) {
      return renderPreviousTable(previousPatientData, onDeletePatient, onEditPatient);
    } else {
      return renderPreviousTable(previousSpouseData, onDeleteSpouse, onEditSpouse);
    }
  };

  const renderPreviousTable = (data, onDelete, onEdit) => {
    return (
      <MDBox mb={4}>
        <Typography variant="h6" gutterBottom sx={{ color: '#00798E', fontWeight: 'bold' }}>
          Existing Surgical History Records
        </Typography>
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Surgery</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Surgery Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Notes</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: '#00798E' }}>Actions</TableCell>
              </TableRow>
              {data?.length ? (
                data.map((row, idx) => {
                  const isEditing = editingRecord?.id === row.id;
                  
                  return (
                  <TableRow
                    key={row.id || idx}
                    sx={{
                      backgroundColor: isEditing ? 'rgba(0, 121, 142, 0.06)' : 'inherit',
                      borderLeft: isEditing ? '3px solid #00798E' : '3px solid transparent',
                      '&:nth-of-type(odd)': { backgroundColor: isEditing ? 'rgba(0, 121, 142, 0.06)' : '#fafafa' },
                      '&:hover': { backgroundColor: isEditing ? 'rgba(0, 121, 142, 0.09)' : '#e3f2fd' },
                      '& > .MuiTableCell-root': { verticalAlign: 'top', pt: isEditing ? 1.5 : 1, pb: isEditing ? 1.5 : 1 },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <TableCell>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editingRecord.param_group_member_option_id || ''}
                            onChange={(e) => {
                              const selected = opdParams.find(opt => opt.param_group_member_option_id === e.target.value);
                              setEditingRecord({
                                ...editingRecord,
                                param_group_member_option_id: e.target.value,
                                param_group_member_id: selected?.param_group_member_id || editingRecord.param_group_member_id,
                                surgery: selected?.label || '',
                                value: selected?.value || ''
                              });
                            }}
                            sx={{ height: 40 }}
                          >
                            {opdParams?.map((option) => (
                              <MenuItem key={option.param_group_member_option_id} value={option.param_group_member_option_id}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        row.surgery
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MDDatePicker
                            value={editingRecord.history_date || ''}
                            onChange={(date) => setEditingRecord({...editingRecord, history_date: date})}
                            renderInput={(params) => (
                              <TextField 
                                {...params} 
                                size="small" 
                                fullWidth 
                                placeholder="Optional - Surgery Date"
                                helperText="Leave empty if date unknown"
                                inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                              />
                            )}
                          />
                          {editingRecord.history_date && (
                            <IconButton
                              size="small"
                              onClick={() => setEditingRecord({...editingRecord, history_date: ''})}
                              title="Clear date"
                              sx={{ color: '#d32f2f' }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ) : (
                        formatDate(row.history_date)
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <RichTextEditor
                          value={editingRecord.notes || ''}
                          onChange={(value) => setEditingRecord({...editingRecord, notes: value})}
                        />
                      ) : (
                        <Box 
                          sx={{ 
                            maxWidth: 200, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            '& p': { margin: 0 },
                            '& ul, & ol': { margin: 0, paddingLeft: '20px' }
                          }}
                          dangerouslySetInnerHTML={{ __html: row.notes || 'N/A' }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {isEditing ? (
                        <>
                          <IconButton
                            aria-label="save"
                            color="success"
                            size="small"
                            onClick={async () => {
                              console.log('Save button clicked, editing record:', editingRecord);
                              console.log('onEdit function:', onEdit);
                              const result = await swal.fire({
                                title: 'Save Changes?',
                                text: 'Do you want to update this record?',
                                icon: 'question',
                                showCancelButton: true,
                                confirmButtonColor: '#00798E',
                                cancelButtonColor: '#999',
                                confirmButtonText: 'Yes, update it!'
                              });
                              if (result.isConfirmed) {
                                console.log('User confirmed, calling onEdit with:', editingRecord);
                                onEdit?.(editingRecord);
                                setEditingRecord(null);
                              }
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: '#e8f5e9',
                                transform: 'scale(1.1)',
                                transition: 'all 0.2s ease'
                              }
                            }}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label="cancel"
                            color="default"
                            size="small"
                            onClick={() => setEditingRecord(null)}
                            sx={{
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                                transform: 'scale(1.1)',
                                transition: 'all 0.2s ease'
                              }
                            }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            aria-label="edit"
                            size="small"
                            onClick={() => setEditingRecord({...row})}
                            sx={{
                              color: '#00798E',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 121, 142, 0.08)',
                                transform: 'scale(1.1)',
                                transition: 'all 0.2s ease'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label="delete"
                            color="error"
                            size="small"
                            onClick={async () => {
                              const result = await swal.fire({
                                title: 'Are you sure?',
                                text: 'Do you want to delete this record?',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#d33',
                                cancelButtonColor: '#3085d6',
                                confirmButtonText: 'Yes, delete it!'
                              });
                              if (result.isConfirmed) {
                                console.log('Row data for deletion:', row); // Debug log
                                const deletePayload = {
                                  contact_id: row.contact_id,
                                  param_group_id: row.param_group_id,
                                  param_group_member_id: row.param_group_member_id,
                                  contact_history_id: row.id
                                };
                                console.log('Delete payload:', deletePayload); // Debug log
                                onDelete?.(deletePayload);
                              }
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: '#ffebee',
                                transform: 'scale(1.1)',
                                transition: 'all 0.2s ease'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
                })
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={4} 
                    align="center" 
                    sx={{ 
                      color: '#999', 
                      fontStyle: 'italic', 
                      padding: '32px 16px', 
                      fontSize: '1rem' 
                    }}
                  >
                    No previous records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };


  const renderRows = (rows, tabIdx) =>
    rows.map((row, idx) => (
      <React.Fragment key={idx}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Surgery</InputLabel>
            <Select
              value={row['surgical-history']}
              label="Surgery"
              onChange={(e) => {
                const selectedOption = opdParams.find(opt => opt.value === e.target.value);
                if (selectedOption) {
                  handleChange(tabIdx, idx, 'surgical-history', selectedOption.value);
                  handleChange(tabIdx, idx, 'param_group_member_id', selectedOption.param_group_member_id);
                  handleChange(tabIdx, idx, 'param_group_member_option_id', selectedOption.param_group_member_option_id);
                  handleChange(tabIdx, idx, 'param_group_id', selectedOption.param_group_id);
                }
              }}
              required
              disabled={loading}
              sx={{ height: selectBoxHeight }}
            >
              {opdParams.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MDDatePicker
              label="Surgery Date (Optional)"
              value={row['history_date'] || ''}
              onChange={(apiDate) => handleChange(tabIdx, idx, 'history_date', apiDate)}
              textFieldProps={{
                fullWidth: true,
                size: 'small',
                disabled: loading,
                InputLabelProps: { shrink: true },
                placeholder: "Optional - Surgery Date",
                helperText: "Leave empty if date unknown"
              }}
              disableFuture={false}
            />
            {row['history_date'] && (
              <IconButton
                size="small"
                onClick={() => handleChange(tabIdx, idx, 'history_date', '')}
                title="Clear date"
                sx={{ color: '#d32f2f' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <RichTextEditor
            value={row['surgical-notes']}
            onChange={(value) => handleChange(tabIdx, idx, 'surgical-notes', value)}
            label="Surgical Notes"
            placeholder="Optional - Additional surgical notes"
            height="80px"
          />
        </Grid>
        <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => handleAddRow(tabIdx)}
            disabled={loading}
            aria-label="Add row"
            sx={{ color: '#2e7d32', bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' } }}
          >
            <AddCircleOutlineIcon fontSize="large" />
          </IconButton>
          {rows.length > 1 && (
            <IconButton
              onClick={() => handleRemoveRow(tabIdx, idx)}
              disabled={loading}
              aria-label="Remove row"
              sx={{ color: '#c62828', bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}
            >
              <RemoveCircleOutlineIcon fontSize="large" />
            </IconButton>
          )}
        </Grid>
      </React.Fragment>
    ));

  const handleGenericTabChange = (event, newValue) => setGenericTab(newValue);
  return (
    <MuiBox>
      <Tabs value={genericTab} onChange={handleGenericTabChange} sx={{ mb: 2 }}>
        <Tab icon={<PersonIcon sx={{ mr: 1 }} />} iconPosition="start" label="Patient" />
        <Tab
          icon={<FavoriteIcon sx={{ mr: 1, color: '#e91e63' }} />}
          iconPosition="start"
          label="Spouse"
        />
      </Tabs>
      
      {/* Load Draft Button */}
      <MuiBox sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {genericTab === 0 ? (
          <LoadDraftButton
            hasDraftData={patientDraft.hasDraftData}
            onLoadDraft={handleLoadPatientDraft}
            onClearDraft={async () => await patientDraft.clearDraft()}
            timestamp={patientDraft.getFormattedTimestamp()}
          />
        ) : (
          <LoadDraftButton
            hasDraftData={spouseDraft.hasDraftData}
            onLoadDraft={handleLoadSpouseDraft}
            onClearDraft={async () => await spouseDraft.clearDraft()}
            timestamp={spouseDraft.getFormattedTimestamp()}
          />
        )}
      </MuiBox>
      
      {/* Previous Data Table */}
      {renderPreviousData()}
      
      <MDBox mb={2} mt={1} sx={{ borderBottom: '1px solid #ccc', pb: 1 }}>
        <h6 style={{ margin: 0, fontWeight: 600, color: '#00798E',  }}>
          Add Surgery History
        </h6>
      </MDBox>
      
      <form onSubmit={handleFormSubmit}>
        <Grid container spacing={2} alignItems="center">
          {genericTab === 0 ? renderRows(rowsPatient, 0) : renderRows(rowsSpouse, 1)}
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="center" gap={2} mt={2}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '10px 24px',
                  background: '#00798E',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                Save Surgical History
              </button>
            </MDBox>
          </Grid>
        </Grid>
      </form>
    </MuiBox>
  );
}

SurgicalHistoryForm.propTypes = {
  patientId: PropTypes.number,
  spouseId: PropTypes.number,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  handleClear: PropTypes.func.isRequired,
  previousPatientData: PropTypes.array,
  previousSpouseData: PropTypes.array,
  onDeletePatient: PropTypes.func,
  onDeleteSpouse: PropTypes.func,
  onEditPatient: PropTypes.func,
  onEditSpouse: PropTypes.func,
  opdParams: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default SurgicalHistoryForm;
